import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { Info } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { QRCodeSVG } from 'qrcode.react';

import { OtpPopup as CustomOtpPopup } from '../../components/common/popup/otp-popup';
import {
    fetchOffers,
    getLoanApplicationFormParameters,
    LoanApplicationFormParameters,
    postLoanApplication,
    PostLoanApplicationRequest, sendOtp, SendOtpRequest,
    putLoanApplication, ApiPutLoanApplicationRequest
} from '../../actions/services';
import {
    decrementLoadingCount,
    hideMessage,
    incrementLoadingCount,
    showMessage,
    getUserInfo,
} from '../../store/globalSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { LoanApplicationFormValues, createLoanApplicationSchema } from '../../schemas/loanApplicationSchema';
import styles from './LoanApplicationPage.module.scss';
import { Button } from '../../components/common/dynamic-form/base/Button/Button';
import { PhoneInput } from '../../components/common/dynamic-form/base/PhoneInput/PhoneInput';
import { Input } from '../../components/common/dynamic-form/base/Input/Input';
import { DatePicker } from '../../components/common/dynamic-form/base/DatePicker/DatePicker';
import { Checkbox } from '../../components/common/dynamic-form/base/Checkbox/Checkbox';
import { Select } from '../../components/common/dynamic-form/base/Select/Select';
import logo from '../../assets/Icons/logo.svg';
import backArrow from '../../assets/Icons/arrow-left.svg';
import closeIcon from '../../assets/Icons/close.svg';


// --- Sub-components (Header, CompanyInfo, SectionHeader, OtpInput) remain unchanged ---
export const Header = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div className={styles.header}>
        <h1>{title}</h1>
        <p>{subtitle}</p>
    </div>
);

export const CompanyInfo = ({ companyName, ownerName, vkn }: { companyName: string; ownerName: string; vkn: string }) => (
    <div className={styles.companyInfo}>
        <h2>{companyName}</h2>
        <p><span>İşletme Sahibi:</span> {ownerName}</p>
        <p><span>TCKN/VKN:</span> {vkn}</p>
    </div>
);

export const SectionHeader = ({ title }: { title: string }) => (
    <h3 className={styles.sectionHeader}>{title}</h3>
);

export const OtpInput = ({ onComplete }: { onComplete: (code: string) => void }) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const inputs = React.useRef<(HTMLInputElement | null)[]>([]);

    const handleChange = (value: string, index: number) => {
        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1);
        setOtp(newOtp);

        if (value && index < 5) {
            inputs.current[index + 1]?.focus();
        }

        if (newOtp.every(val => val !== '')) {
            onComplete(newOtp.join(''));
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputs.current[index - 1]?.focus();
        }
    };

    return (
        <div className={styles.otpContainer}>
            {otp.map((digit, idx) => (
                <input
                    key={idx}
                    ref={el => (inputs.current[idx] = el)}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleChange(e.target.value, idx)}
                    onKeyDown={e => handleKeyDown(e, idx)}
                    className={styles.otpInput}
                />
            ))}
        </div>
    );
};

const QrRedirectModal = ({ message, qrData, onClose }: { message: string, qrData: string, onClose: () => void }) => (
    <div className={styles.modalOverlay}>
        <div className={`${styles.modalContent} ${styles.qrModalContent}`}>
            <button
                type="button"
                onClick={onClose}
                className={styles.qrCloseButton}
                aria-label="Kapat"
            >
                <img src={closeIcon} alt="Kapat" />
            </button>
            <h2 className={styles.qrModalTitle}>Mobil Uygulamada Devam Et</h2>
            <p className={styles.qrModalBody}>{message}</p>
            <div className={styles.qrCodeWrapper}>
                <QRCodeSVG value={qrData} size={220} />
            </div>
            <p className={styles.qrModalHint}>QR kodu telefonunuzun kamerası veya banka uygulamasıyla okutun.</p>
        </div>
    </div>
);

const LinkPopup = ({ message, url, onClose }: { message: string, url: string, onClose: () => void }) => {
    const handleGoToPage = () => {
        window.open(url, '_blank');
        onClose();
    };
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2>İşlem Devam Ediyor</h2>
                <p>{message}</p>
                <div style={{ display: 'flex', gap: '1rem', width: '100%', marginTop: '1rem' }}>
                    <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>İptal</Button>
                    <Button type="button" variant="primary" onClick={handleGoToPage} style={{ flex: 1 }}>Sayfaya Git</Button>
                </div>
            </div>
        </div>
    );
};

const OtpPopup = ({ message, onComplete, onClose }: { message: string, onComplete: (code: string) => void, onClose: () => void }) => (
    <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
            <h2>Doğrulama Kodu</h2>
            <p>{message}</p>
            <OtpInput onComplete={(code) => { onComplete(code); }} />
            <button type="button" className={styles.resendBtn} style={{ marginTop: '1.5rem' }}>Tekrar Kod Gönder</button>
            <Button type="button" variant="secondary" onClick={onClose} style={{ marginTop: '1rem', width: '100%' }}>İptal</Button>
        </div>
    </div>
);
const LenderResultPopup = ({ message, targetUrl, buttonText, isSuccess, onClose }: { message: string, targetUrl?: string | null, buttonText?: string, isSuccess: boolean, onClose: () => void }) => {
    const handleButtonClick = () => {
        if (targetUrl) {
            window.open(targetUrl, '_blank');
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} style={{ position: 'relative', paddingTop: '.48rem', maxWidth: '5.3rem' }}>
                <button type="button" onClick={onClose} style={{ position: 'absolute', top: '.16rem', right: '.16rem', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                    <img src={closeIcon} alt="Kapat" style={{ width: '.24rem', height: '.24rem' }} />
                </button>
                <h2>Bilgilendirme</h2>
                <p>{message}</p>
                {isSuccess && buttonText && (
                    <Button
                        type="button"
                        onClick={handleButtonClick}
                        style={{
                            width: '100%',
                            backgroundColor: '#FA0050',
                            color: '#fff',
                            border: 'none'
                        }}
                    >
                        {buttonText}
                    </Button>
                )}
            </div>
        </div>
    );
};

// ----------------------------------------------------------------------

type ActionState =
    | { type: 'IDLE' }
    | { type: 'OTP', message: string }
    | { type: 'CUSTOM_OTP', message: string }
    | { type: 'QR', message: string, data: string }
    | { type: 'QR_REDIRECT', message: string, mobileUrl: string }
    | { type: 'LINK', message: string, url: string }
    | { type: 'REQUEST_OTP' }
    | { type: 'LENDER_RESULT', message: string, targetUrl?: string | null, buttonText?: string, isSuccess: boolean };

type Consent = {
    consentId: string;
    documentUrl: string;
    type: string;
    documentContent?: string;
    mandatory: boolean;
    isApproved: boolean;
};

export const LoanApplicationPage = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const userInfo = useAppSelector(getUserInfo);



    const [params, setParams] = useState<LoanApplicationFormParameters | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionState, setActionState] = useState<ActionState>({ type: 'IDLE' });
    const [applicationId, setApplicationId] = useState<string | null>(null);
    const [maxAmount, setMaxAmount] = useState<number>(Infinity);
    const [consentPopupContent, setConsentPopupContent] = useState<{ title: string; content: string; onCloseAction?: () => void } | null>(null);
    const [putLoanAppData, setPutLoanAppData] = useState<Omit<ApiPutLoanApplicationRequest, 'otp'> | null>(null);

    // YENİ EKLENEN STATE: Görüntülenen consent ID'lerini tutar
    const [viewedConsents, setViewedConsents] = useState<string[]>([]);

    const schema = React.useMemo(() => createLoanApplicationSchema(maxAmount), [maxAmount]);

    const { register, handleSubmit, control, formState: { errors, isValid }, reset, watch, setValue } = useForm<LoanApplicationFormValues>({
        resolver: zodResolver(schema) as any,
        mode: 'onChange',
    });

    const amountString = watch('requestedAmount') || '';

    const reasonOptions = [
        { value: '', label: 'Başvuru sebebi seçiniz' },
        { value: 'İşletme sermayesi ve günlük giderler (Kira, maaş, faturalar ve diğer düzenli işletme giderleri)', label: 'İşletme sermayesi ve günlük giderler (Kira, maaş, faturalar ve diğer düzenli işletme giderleri)' },
        { value: 'Stok, ürün veya hammadde alımı (Gıda malzemesi, içecek, ambalaj ve diğer stok ihtiyaçları)', label: 'Stok, ürün veya hammadde alımı (Gıda malzemesi, içecek, ambalaj ve diğer stok ihtiyaçları)' },
        { value: 'Ekipman veya demirbaş alımı/yenileme', label: 'Ekipman veya demirbaş alımı/yenileme' },
        { value: 'Tadilat veya iş yeri yenileme', label: 'Tadilat veya iş yeri yenileme' },
        { value: 'Yeni şube açma, kapasite artırma veya işi büyütme', label: 'Yeni şube açma, kapasite artırma veya işi büyütme' },
        { value: 'Araç, kurye veya lojistik ihtiyacı', label: 'Araç, kurye veya lojistik ihtiyacı' },
        { value: 'Pazarlama, teknoloji veya dijitalleşme yatırımı', label: 'Pazarlama, teknoloji veya dijitalleşme yatırımı' },
        { value: 'Mevcut ticari borçları kapatma veya yeniden yapılandırma', label: 'Mevcut ticari borçları kapatma veya yeniden yapılandırma' }
    ];

    const watchedConsents = watch('consents') as Consent[];

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const searchParams = new URLSearchParams(location.search);
        const offerId = searchParams.get('offerId');
        const identityNumber = searchParams.get('identityNumber');

        if (!identityNumber || !offerId) {
            setError("Gerekli parametreler (identityNumber veya offerId) bulunamadı.");
            setLoading(false);
            return;
        }

        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const [data, offersData] = await Promise.all([
                    getLoanApplicationFormParameters(identityNumber, offerId),
                    fetchOffers(signal).catch(() => null)
                ]);

                if (signal.aborted) return;

                if (offersData && offersData.offers) {
                    const currentOffer = offersData.offers.find(o => String(o.offerId) === String(offerId));
                    if (currentOffer && currentOffer.amount?.totalAmount) {
                        setMaxAmount(Number(currentOffer.amount.totalAmount));
                    }
                }
                setParams(data);

            } catch (err: any) {
                // Rate limit (429) ve Yetki (403) durumlarını global bffApiCaller yönetsin
                if (err?.response?.status === 429 || err?.response?.status === 403) return;

                if (err.name !== 'CanceledError' && !signal.aborted) {
                    console.error("Failed to load application parameters:", err);

                    let errorMessage = "Başvuru form bilgileri yüklenirken bir hata oluştu.";
                    if (err.response?.data?.message) {
                        const msgData = err.response.data.message;
                        errorMessage = Array.isArray(msgData) ? msgData.join('\n') : msgData;
                    }

                    dispatch(showMessage({
                        title: "Hata",
                        text: errorMessage,
                        btnPrimaryText: "Kapat",
                        btnPrimaryClassNames: "popup-btn-pink",
                        btnPrimaryCallback: () => {
                            dispatch(hideMessage());
                            history.push('/identity-verification', { offerId });
                        }
                    }));
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchInitialData();

        return () => {
            controller.abort();
        };
    }, [location.search]);

    useEffect(() => {
        if (params) {
            let initialPhoneNumber = '';
            if (params.vendor?.phoneNumber) {
                const rawPhone = params.vendor.phoneNumber;
                let cleanPhone = rawPhone.replace(/[^\d*]/g, '');
                if (cleanPhone.length === 10 && !cleanPhone.startsWith('90')) {
                    cleanPhone = '90' + cleanPhone;
                }
                initialPhoneNumber = cleanPhone;
            }

            // Doğum tarihini Backend'den (YYYY-MM-DD) alıp Frontend için (DD-MM-YYYY) formatına çeviriyoruz
            let initialBirthDate = params.vendor?.birthDate === "null" ? '' : params.vendor?.birthDate || '';
            if (initialBirthDate && !initialBirthDate.includes('*')) {
                initialBirthDate = initialBirthDate.split('T')[0]; // Remove timestamp if exists

                // Noktaları tireye çevir (Örn: 11.01.1999 -> 11-01-1999)
                initialBirthDate = initialBirthDate.replace(/\./g, '-');

                if (initialBirthDate.includes('-')) {
                    const parts = initialBirthDate.split('-');
                    // Eğer format YYYY-MM-DD ise (ilk parça 4 haneliyse), DD-MM-YYYY'ye çevir
                    if (parts.length === 3 && parts[0].length === 4) {
                        initialBirthDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                }
            }

            reset({
                phoneNumber: initialPhoneNumber,
                birthDate: initialBirthDate,
                requestedAmount: '',
                applicationReason: '',
                consents: params.requiredConsents.map(c => ({
                    consentId: c.consentId.toString(),
                    documentUrl: c.documentUrl,
                    type: c.type || '',
                    documentContent: c.documentContent || '',
                    mandatory: c.mandatory,
                    isApproved: false,
                })),
            });
        }
    }, [params, reset]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            console.log("Form Validation Errors:", errors);
        }
    }, [errors]);

    const handleOtpVerify = async (code: string) => {
        console.log("Verifying OTP:", code);

        // OTP kodu girilir girilmez popup'ı kapat
        setActionState({ type: 'IDLE' });

        if (actionState.type === 'CUSTOM_OTP' && putLoanAppData) {
            dispatch(incrementLoadingCount());
            try {
                const request: ApiPutLoanApplicationRequest = {
                    ...putLoanAppData,
                    otp: code
                };
                const response = await putLoanApplication(request);

                if (response.code === "00" || response.message === "Success") {
                    const redirect = response.redirect;

                    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                    const isDesktop = !isMobileDevice;

                    const mobileUrl = redirect?.mobileUrl ?? null;
                    const webUrl = redirect?.webUrl ?? null;
                    const redirectType = redirect?.type;

                    dispatch(hideMessage());

                    const lenderCode = response.lenderResponse?.code;

                    if (lenderCode === "1") {
                        // code=1: redirect.type'a göre ikincil karar
                        // WEB  → webUrl yeni sekmede (mevcut web popup)
                        // MOBILE / BOTH → Desktop: QR modal | Mobile: deeplink butonu
                        const msg = "Ön değerlendirmeniz olumlu sonuçlandı. Başvurunuzu tamamlamak için Banka kanalından devam etmeniz gerekmektedir.";
                        if (redirectType === 'WEB') {
                            setActionState({
                                type: 'LENDER_RESULT',
                                message: msg,
                                targetUrl: webUrl,
                                buttonText: "Başvuruya Devam Et",
                                isSuccess: true,
                            });
                        } else if (redirectType === 'MOBILE' || redirectType === 'BOTH') {
                            if (isDesktop && mobileUrl) {
                                setActionState({ type: 'QR_REDIRECT', message: msg, mobileUrl });
                            } else if (isMobileDevice && mobileUrl) {
                                setActionState({
                                    type: 'LENDER_RESULT',
                                    message: msg,
                                    targetUrl: mobileUrl,
                                    buttonText: "Başvuruya Devam Et",
                                    isSuccess: true,
                                });
                            } else if (webUrl) {
                                // mobileUrl yoksa webUrl'e düş
                                setActionState({
                                    type: 'LENDER_RESULT',
                                    message: msg,
                                    targetUrl: webUrl,
                                    buttonText: "Başvuruya Devam Et",
                                    isSuccess: true,
                                });
                            } else {
                                setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: null, buttonText: undefined, isSuccess: true });
                            }
                        } else {
                            // redirect.type bilinmiyorsa: mobileUrl varsa QR/deeplink, yoksa webUrl
                            if (isDesktop && mobileUrl) {
                                setActionState({ type: 'QR_REDIRECT', message: msg, mobileUrl });
                            } else if (isMobileDevice && mobileUrl) {
                                setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: mobileUrl, buttonText: "Başvuruya Devam Et", isSuccess: true });
                            } else if (webUrl) {
                                setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: webUrl, buttonText: "Başvuruya Devam Et", isSuccess: true });
                            } else {
                                setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: null, buttonText: undefined, isSuccess: true });
                            }
                        }

                    } else if (lenderCode === "2") {
                        // code=2: Her zaman QR (desktop) / deeplink butonu (mobile)
                        const msg = "Ön değerlendirmeniz olumlu sonuçlandı. Kredi başvurunuzu tamamlamak için Banka Mobil uygulamasından devam etmeniz gerekmektedir.";
                        if (isDesktop && mobileUrl) {
                            setActionState({ type: 'QR_REDIRECT', message: msg, mobileUrl });
                        } else if (isMobileDevice && mobileUrl) {
                            setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: mobileUrl, buttonText: "Başvuruya Devam Et", isSuccess: true });
                        } else {
                            setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: null, buttonText: undefined, isSuccess: true });
                        }

                    } else if (lenderCode === "4") {
                        // code=4: Her zaman QR (desktop) / deeplink butonu (mobile)
                        const msg = "Kredi başvurunuza devam edebilmeniz için bazı bilgilerinizi Banka Mobil üzerinden tamamlamanız gerekmektedir.";
                        if (isDesktop && mobileUrl) {
                            setActionState({ type: 'QR_REDIRECT', message: msg, mobileUrl });
                        } else if (isMobileDevice && mobileUrl) {
                            setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: mobileUrl, buttonText: "Başvuruya Devam Et", isSuccess: true });
                        } else {
                            setActionState({ type: 'LENDER_RESULT', message: msg, targetUrl: null, buttonText: undefined, isSuccess: true });
                        }

                    } else if (lenderCode === "11") {
                        // code=11: Akbank şube bulucu sayfası her zaman yeni sekmede açılır
                        setActionState({
                            type: 'LENDER_RESULT',
                            message: "Kredi başvurunuza devam edebilmeniz için bir Akbank şubesine gitmeniz gerekiyor. Size en yakın şubeden işleminizi tamamlayabilirsiniz.",
                            targetUrl: "https://www.akbank.com/sube-ve-atmler",
                            buttonText: "En Yakın Şubeyi Bul",
                            isSuccess: true,
                        });

                    } else {
                        setActionState({
                            type: 'LENDER_RESULT',
                            message: response.lenderResponse?.message || "Başvurunuz alınmıştır.",
                            targetUrl: null,
                            buttonText: undefined,
                            isSuccess: true,
                        });
                    }
                } else {
                    dispatch(hideMessage());
                    setActionState({
                        type: 'LENDER_RESULT',
                        message: response.lenderResponse?.message || response.message || "Doğrulama başarısız oldu.",
                        targetUrl: null,
                        buttonText: undefined,
                        isSuccess: false
                    });
                }
            } catch (err: any) {
                const isHandledGlobally = err?.response?.status === 429 || err?.response?.status === 403 || (err?.response?.config?.url?.includes('/v1/vendor/') && err?.response?.data?.message);
                if (isHandledGlobally) return;

                console.error("PUT loan application failed:", err);
                dispatch(showMessage({
                    title: "Hata",
                    text: "Doğrulama işlemi sırasında bir hata oluştu.",
                    btnPrimaryText: "Tamam",
                    btnPrimaryClassNames: "popup-btn-pink"
                }));
            } finally {
                dispatch(decrementLoadingCount());
            }
        } else {
            dispatch(hideMessage());
            dispatch(showMessage({
                title: "Başarılı",
                text: "Başvurunuz alınmıştır.",
                btnPrimaryText: "Tamam"
            }));
        }
    };

    const onSubmit = async (data: LoanApplicationFormValues) => {
        if (!params) return;
        dispatch(incrementLoadingCount());
        try {
            const searchParams = new URLSearchParams(location.search);
            const tcIdentityNumber = searchParams.get('identityNumber');

            const cleanedFormData: { [key: string]: any } = {
                trIdentificationNumber: tcIdentityNumber,
                ownerName: params.vendor.ownerFullName,
                legalName: params.vendor.legalName,
                requestedCreditAmount: Number(parseFloat(data.requestedAmount.replace(/\./g, '').replace(/,/g, '.')).toFixed(2)),
                applicationReason: data.applicationReason
            };

            if (data.phoneNumber && !data.phoneNumber.includes('*')) {
                cleanedFormData.phoneNumber = data.phoneNumber;
            }

            let formattedBirthDate = data.birthDate;
            // UI'dan gelen Doğum Tarihini (DD-MM-YYYY) API için (YYYY-MM-DD) formatına çeviriyoruz
            if (data.birthDate && !data.birthDate.includes('*')) {
                const dateParts = data.birthDate.split('-');
                // Eğer format DD-MM-YYYY ise (ilk parça gün yani 2 haneliyse)
                if (dateParts.length === 3 && dateParts[0].length === 2) {
                    formattedBirthDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
                }
                cleanedFormData.birthDate = formattedBirthDate;
            }

            const newConsents: PostLoanApplicationRequest['consents'] = data.consents.map(c => ({
                consentId: c.consentId,
                documentUrl: c.documentUrl,
                mandatory: c.mandatory,
                isApproved: c.isApproved,
                approvedAt: new Date().toISOString(),
                type: (c as any).type,
                documentContent: (c as any).documentContent
            }));
            const request: PostLoanApplicationRequest = {
                offerId: params.offerId,
                userId: params.vendor.userId,
                formData: cleanedFormData as PostLoanApplicationRequest['formData'],
                consents: newConsents
            };

            const response = await postLoanApplication(request);

            const responseCode = response.code || "";
            const responseMessage = response.message || "";

            if (responseCode === "SMS_OTP_REQUIRED" || responseCode === "OTP_REQUIRED") {
                setActionState({ type: 'OTP', message: responseMessage || "Lütfen telefonunuza gelen 6 haneli doğrulama kodunu giriniz." });
            } else if (responseCode === "INVALID_CAPTCHA_TOKEN") {
                dispatch(showMessage({
                    title: "Güvenlik Hatası",
                    text: responseMessage || "Geçersiz güvenlik doğrulaması. Lütfen sayfayı yenileyip tekrar deneyin.",
                    btnPrimaryText: "Tamam",
                    btnPrimaryClassNames: "secondary"
                }));
            } else if (responseCode.includes("REDIRECT")) {
                if (response.url) {
                    window.location.replace(response.url);
                } else {
                    throw new Error("Yönlendirme bağlantısı bulunamadı.");
                }
            } else if (responseCode.includes("LINK")) {
                if (response.url) {
                    setActionState({ type: 'LINK', message: responseMessage || "İşleminize devam etmek için ilgili sayfaya yönlendirileceksiniz.", url: response.url });
                } else {
                    throw new Error("Bağlantı adresi bulunamadı.");
                }
            } else if (responseCode.includes("QR")) {
                if (response.qrData || response.url) {
                    setActionState({ type: 'QR', message: responseMessage || "Lütfen aşağıdaki QR kodu banka veya ödeme uygulamanızdan okutunuz.", data: response.qrData || response.url || '' });
                } else {
                    throw new Error("QR Kod verisi bulunamadı.");
                }
            } else if (responseCode === "00" || responseMessage === "Success") {
                if (response.applicationId) {
                    dispatch(showMessage({ title: "Yönlendiriliyor...", text: "OTP sayfasına yönlendiriliyorsunuz..." }));

                    const otpRequest: SendOtpRequest = {
                        applicationId: response.applicationId,
                        trIdentificationNumber: tcIdentityNumber!,
                        birthDate: formattedBirthDate,
                        phoneCountryCode: data.phoneNumber.substring(0, 2),
                        phoneAreaCode: data.phoneNumber.substring(2, 5),
                        phoneNumber: data.phoneNumber.substring(5),
                        gdprConfirmation: "true",
                        languageCode: "TR",
                    };

                    const otpResponse = await sendOtp(otpRequest);
                    dispatch(hideMessage());

                    if (otpResponse.code === "00" || otpResponse.message === "Success") {
                        const formDataDto = {
                            trIdentificationNumber: cleanedFormData.trIdentificationNumber,
                            phoneNumber: cleanedFormData.phoneNumber,
                            birthDate: cleanedFormData.birthDate,
                            requestedCreditAmount: cleanedFormData.requestedCreditAmount.toString(),
                            applicationReason: cleanedFormData.applicationReason
                        };
                        const consentsDto = newConsents.map(c => ({
                            consentId: c.consentId,
                            isApproved: c.isApproved.toString()
                        }));

                        setPutLoanAppData({
                            applicationId: otpResponse.applicationId || response.applicationId || '',
                            userId: params.vendor.userId,
                            offerId: otpResponse.offerId || params.offerId,
                            formData: formDataDto,
                            consents: consentsDto,
                            languageCode: "TR",
                        });
                        setActionState({ type: 'CUSTOM_OTP', message: "Lütfen telefonunuza gelen doğrulama kodunu giriniz." });
                    } else {
                        dispatch(showMessage({ title: "Hata", text: otpResponse.message || "OTP gönderilemedi." }));
                    }
                } else {
                    dispatch(showMessage({
                        title: "Başarılı",
                        text: responseMessage || "Başvurunuz başarıyla iletildi.",
                        btnPrimaryText: "Tamam"
                    }));
                }
            }
        } catch (err: any) {
            const isHandledGlobally = err?.response?.status === 429 || err?.response?.status === 403 || (err?.response?.config?.url?.includes('/v1/vendor/') && err?.response?.data?.message);
            if (isHandledGlobally) return;

            console.error("Submission failed:", err);
            dispatch(showMessage({
                title: "Hata",
                text: "Başvuru sırasında bir hata oluştu. Lütfen tekrar deneyiniz.",
                btnPrimaryText: "Tamam",
                btnPrimaryClassNames: "popup-btn-pink"
            }));
        } finally {
            dispatch(decrementLoadingCount());
        }
    };

    const handleAmountInput = (e: React.FormEvent<HTMLInputElement>) => {
        // İzin verilen karakterler: rakamlar ve virgül
        let value = e.currentTarget.value.replace(/[^0-9,]/g, '');

        // Sadece bir virgüle izin ver
        const parts = value.split(',');
        if (parts.length > 2) {
            value = parts[0] + ',' + parts.slice(1).join('');
        }

        // Virgülden sonra en fazla 2 haneye izin ver
        if (parts.length >= 2 && parts[1].length > 2) {
            value = parts[0] + ',' + parts[1].substring(0, 2);
        }

        // Binlik ayracı (nokta) ekle
        let [integerPart, decimalPart] = value.split(',');
        if (integerPart) {
            // Başındaki sıfırları at (0 hariç)
            integerPart = parseInt(integerPart, 10).toString();
            if (integerPart === 'NaN') integerPart = '';
            // Binlik ayraçları koy
            integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        } else if (value.startsWith(',')) {
            integerPart = '0';
        }

        if (value.includes(',')) {
            e.currentTarget.value = `${integerPart},${decimalPart !== undefined ? decimalPart : ''}`;
        } else {
            e.currentTarget.value = integerPart || '';
        }
    };

    const onlySafeText = (e: React.FormEvent<HTMLInputElement>) => {
        e.currentTarget.value = e.currentTarget.value.replace(/[<>]/g, '');
    };
    const ConsentPopup = ({ title, content, onClose }: { title: string, content: string, onClose: () => void }) => (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
                <h2 style={{ flexShrink: 0 }}>{title}</h2>
                <div
                    dangerouslySetInnerHTML={{ __html: content }}
                    style={{ overflowY: 'auto', flex: 1, paddingRight: '0.25rem' }}
                />
                <div style={{ flexShrink: 0, paddingTop: '1rem' }}>
                    <Button variant="primary" onClick={onClose} style={{ width: '100%', backgroundColor: '#FA0050', color: '#fff', border: 'none' }}>Kapat</Button>
                </div>
            </div>
        </div>
    );


    if (error) return <div style={{ padding: '2rem', textAlign: 'center' }}><h1>Hata</h1><p>{error}</p></div>;

    if (loading && !params) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loader}></div>
                <p>Başvuru bilgileri yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className={styles.pageLayout}>
            <button type="button" onClick={() => {
                const searchParams = new URLSearchParams(location.search);
                const offerId = searchParams.get('offerId');
                const identityNumber = searchParams.get('identityNumber');
                const urlPhoneNumber = searchParams.get('phoneNumber');
                const maskedPhone = searchParams.get('maskedPhone');
                // maskedPhone varsa (orijinal maskeli numara) onu, yoksa URL phoneNumber'ı kullan
                const phoneNumberToPass = maskedPhone || params?.vendor?.phoneNumber || urlPhoneNumber;

                if (offerId && identityNumber && phoneNumberToPass) {
                    history.push('/phone-verification', {
                        offerId,
                        identityNumber,
                        phoneNumber: phoneNumberToPass,
                        maskedPhoneNumber: phoneNumberToPass,
                    });
                } else {
                    history.push('/identity-verification', { offerId });
                }
            }} className={styles.backButton}>
                <img src={backArrow} alt="Geri" />
            </button>
            <Header
                title="Kredi Başvurusu"
                subtitle="Başvuru işlemi, işletme sahibi tarafından yapılabilmektedir."
            />
            <CompanyInfo
                companyName={params?.vendor?.legalName || '-'}
                ownerName={params?.vendor?.ownerFullName || '-'}
                vkn={params?.vendor?.taxNumber || '-'}
            />

            <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
                <SectionHeader title="KİŞİSEL BİLGİLER" />

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Controller
                        control={control}
                        name="phoneNumber"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <PhoneInput
                                id="phone"
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                error={errors.phoneNumber?.message}
                                label="Telefon Numarası"
                                disabled={String(value || '').includes('*')}
                            />
                        )}
                    />
                    {String(watch('phoneNumber') || '').includes('*') && (
                        <button
                            type="button"
                            className={styles.changeBtn}
                            onClick={() => setValue('phoneNumber', '', { shouldValidate: true })}
                        >
                            Bilgiyi Değiştir
                        </button>
                    )}
                </div>

                <p className={styles.infoText}>
                    <Info size={14} /> Kredi başvurusunda kullanacağınız telefon numarasını giriniz.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <DatePicker
                        id="birthDate"
                        {...register('birthDate')}
                        value={watch('birthDate')}
                        error={errors.birthDate?.message}
                        label="Doğum Tarihi"
                        disabled={String(watch('birthDate') || '').includes('*')}
                    />
                    {String(watch('birthDate') || '').includes('*') && (
                        <button
                            type="button"
                            className={styles.changeBtn}
                            onClick={() => setValue('birthDate', '', { shouldValidate: true })}
                        >
                            Bilgiyi Değiştir
                        </button>
                    )}
                </div>

                <Input
                    id="amount"
                    {...register('requestedAmount')}
                    onInput={handleAmountInput}
                    error={errors.requestedAmount?.message}
                    label="İstediğiniz Kredi Tutarı"
                    placeholder="Tutar giriniz"
                    suffix="TL"
                />

                <Select
                    id="reason"
                    {...register('applicationReason')}
                    error={errors.applicationReason?.message}
                    label="Başvuru Sebebi"
                    options={reasonOptions}
                />

                <div className={styles.checkboxGroup}>
                    {watchedConsents?.map((consent, index) => {
                        let consentName = '';
                        let consentText = '';

                        switch (consent.type) {
                            case 'GDPR_CONSENT':
                                consentName = "KVKK Açık Rıza Metni";
                                consentText = "’ni okudum. Kredi başvurusu kapsamında kişisel verilerimin ve Yemeksepeti kaynaklı işletme bilgilerimin işlenmesine ve başvuru ekranında belirtilen banka/finansal kuruluşa aktarılmasına açık rıza veriyorum.";
                                break;
                            case 'PRIVACY_NOTICE':
                                consentName = "Kredi Başvurusu Aydınlatma Metni";
                                consentText = "’ni okudum ve kişisel verilerimin işlenmesi hakkında bilgilendirildim.";
                                break;
                            case 'MARKETING_COMMUNICATION_CONSENT':
                                consentName = "Hakediş Ödeme Bilgileri Bilgilendirme ve Onay Metni";
                                consentText = "’ni okudum ve kabul ediyorum.";
                                break;
                            default:
                                return null;
                        }

                        const showDocument = () => {
                            if (!viewedConsents.includes(consent.consentId)) {
                                setViewedConsents(prev => [...prev, consent.consentId]);
                            }

                            if (consent.documentContent) {
                                setConsentPopupContent({
                                    title: consentName,
                                    content: consent.documentContent
                                });
                            } else if (consent.documentUrl) {
                                window.open(consent.documentUrl, '_blank');
                            }
                        };

                        const handleCheckboxInteraction = (e: React.MouseEvent | React.ChangeEvent) => {
                            if (!viewedConsents.includes(consent.consentId)) {
                                e.preventDefault();
                                showDocument();
                            } else {
                                const updatedConsents = [...watchedConsents];
                                updatedConsents[index] = { ...updatedConsents[index], isApproved: !consent.isApproved };
                                setValue('consents', updatedConsents, { shouldValidate: true });
                            }
                        };

                        const handleLinkClick = (e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            showDocument();
                        };

                        return (
                            <Checkbox
                                key={consent.consentId}
                                checked={consent.isApproved}
                                onChange={handleCheckboxInteraction}
                                label={
                                    <>
                                        <a href="#" onClick={handleLinkClick}>
                                            {consentName}
                                        </a>{consentText}
                                    </>
                                }
                                error={errors.consents?.[index]?.isApproved?.message}
                            />
                        );
                    })}
                </div>

                <div className={styles.footer}>
                    <img src={logo} alt="Yemeksepeti Pay" />
                    <Button type="submit" variant="primary" className={styles.submitBtn} disabled={!isValid || loading || !params}>
                        Devam Et
                    </Button>
                </div>
            </form>
            {consentPopupContent && (
                <ConsentPopup
                    title={consentPopupContent.title}
                    content={consentPopupContent.content}
                    onClose={() => {
                        setConsentPopupContent(null);
                        if (consentPopupContent.onCloseAction) {
                            consentPopupContent.onCloseAction();
                        }
                    }}
                />
            )}

            {actionState.type === 'OTP' && (
                <OtpPopup
                    message={actionState.message}
                    onComplete={handleOtpVerify}
                    onClose={() => setActionState({ type: 'IDLE' })}
                />
            )}
            {actionState.type === 'CUSTOM_OTP' && (
                <CustomOtpPopup
                    message={actionState.message}
                    onComplete={handleOtpVerify}
                    onClose={() => setActionState({ type: 'IDLE' })}
                />
            )}
            {actionState.type === 'QR' && (
                <QrRedirectModal
                    message={actionState.message}
                    qrData={actionState.data}
                    onClose={() => setActionState({ type: 'IDLE' })}
                />
            )}
            {actionState.type === 'QR_REDIRECT' && (
                <QrRedirectModal
                    message={actionState.message}
                    qrData={actionState.mobileUrl}
                    onClose={() => setActionState({ type: 'IDLE' })}
                />
            )}
            {actionState.type === 'LINK' && (
                <LinkPopup
                    message={actionState.message}
                    url={actionState.url}
                    onClose={() => setActionState({ type: 'IDLE' })}
                />
            )}
            {actionState.type === 'LENDER_RESULT' && (
                <LenderResultPopup
                    message={actionState.message}
                    targetUrl={actionState.targetUrl}
                    buttonText={actionState.buttonText}
                    isSuccess={actionState.isSuccess}
                    onClose={() => {
                        setActionState({ type: 'IDLE' });
                        history.push('/loan');
                    }}
                />
            )}
        </div>
    );
};